import { container } from "tsyringe";
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import { AuthPresenter } from "@/app/ui/presenters/AuthPresenter";
import {
  AuthEntities,
  DeviceAuthCheckApprovalEntities,
  DeviceAuthEntities,
  SetupPasswordValidation,
  TokenVerificationAccountEntities,
  Login2faEntity,
  OtpRequestResponseEntity
} from "@/domain/entities/Auth";
import {
  ForgotPasswordApiRequest,
  ResetPasswordApiRequest,
  LoginApiRequest,
  LogoutApiRequest,
  EditPasswordApiRequest,
  VerificationAccountRequest,
  VerificationOTPRequest,
  DeviceAuthApiRequest,
  OtpRequest,
  SetupPasswordValidationApiRequest,
  SetupPasswordRequest,
  Login2faApiRequest,
  OtpRequestLogin2faApiRequest,
  OtpVerifySkipApiRequest,
  OtpVerifyLogin2faApiRequest
} from "@/data/payload/api/AuthRequest";
import router from "../router";
import {
  setCredential,
  getToken,
  setRememberMe,
  removeAuthCredential,
  setHubData,
  setDeviceBearer,
  removeDeviceBearer,
  setTempUserEmail,
  removeTempUserEmail,
  getTempUserEmail,
  removePairingCode,
  setPairingCode,
  setChallengeToken,
  getChallengeToken,
  removeChallengeToken,
} from "@/app/infrastructures/misc/Cookies";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import { MainAppController } from "./MainAppController";
import {
  parsingErrorResponse,
  clearDataLocalStorage,
  clearDataSessionStorage,
  storeDatatoLocalStorage,
  CLIENT_ERROR
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "./AccountController";
import { dataLayer } from "@/app/infrastructures/misc/UtilsGtm";
import { IS_SENDER_ACCOUNT } from "@/app/infrastructures/misc/Utils";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { DistrictData } from "@/domain/entities/Location";
import jwtDecode from "jwt-decode";
import { HubController } from "./HubController";
import { Hub, RequestHubList } from "@/domain/entities/Hub";
import { NotificationController } from "./NotificationController";
import { FirebaseController } from "./FirebaseController";
import { CargoController } from "./CargoController";
import {
  PairTokenRequest,
  UnpairTokenRequest
} from "@/data/payload/api/AccountRequest";
import { FlagsPermissionMiddleware } from "@/feature-flags/flags-middleware";
import { MiddlewareController } from "@/app/ui/controllers/MiddlewareController";
import { flags } from "@/feature-flags";
import { AccountEntities } from "@/domain/entities/Account";
import isAccountBlacklisted from "@/app/infrastructures/misc/common-library/IsAccountBlacklisted";
import { PusatResolusiV2Controller } from "./PusatResolusiV2Controller";
import { storeUserDevice } from "@/app/infrastructures/misc/DeviceToken";
import { STATUS } from "../views/login/constants";
import { BalanceController } from "./BalanceController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { getDeviceID, storeDeviceID } from '@/app/infrastructures/misc/DeviceID';
import { PrintReceiptController } from "./PrintReceiptController";
import { app } from "@/main";
import { getPrimeVueLocaleConfig } from "@/app/infrastructures/misc/common-library/PrimeVueLocaleConfig";
import { syncLocaleFromAccount } from "@/app/infrastructures/misc/syncLocaleFromAccount";
import { resolveApiBilingualMessage } from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { recaptchaV3 } from "@/app/infrastructures/misc/common-library/RecaptchaV3";
import {
  getDataSessionStorage,
  storeDatatoSessionStorage,
  removeDataSessionStorage
} from "@/app/infrastructures/misc/SessionStorage";
import { HelpCenterController } from "./HelpCenterController";

const DISMISSED_ACCOUNT_ALERTS_STORAGE_KEY = "dismissed-account-alerts";

const getDismissedAccountAlerts = (): Record<string, boolean> => {
  try {
    const value = getDataSessionStorage(DISMISSED_ACCOUNT_ALERTS_STORAGE_KEY);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
};

export interface AuthState {
  authData: AuthEntities;
  isLoading: boolean;
  token: any;
  isError: boolean;
  isErrorInternetOrSystem: boolean;
  errorCause: string;
  responsePayload: ResponsePayload | null;
  openModalEditPassword: boolean;
  isErrorForget: boolean;
  openModalSuccess: boolean;
  openModalFailed: boolean;
  closeModal: boolean;
  isErrorWrongEmail: boolean;
  isErrorWrongPassword: boolean;
  dismissedAccountAlerts: Record<string, boolean>;
}

@Module({ namespaced: true, dynamic: true, store, name: "auth" })
class AuthStore extends VuexModule implements AuthState {
  public authData = new AuthEntities();
  public isLoading = false;
  public token = getToken() || "";
  public isError = false;
  public isErrorInternetOrSystem = false;
  public errorCause = "";
  public isErrorForget = false;
  public openModalSuccess = false;
  public openModalFailed = false;
  public closeModal = false;
  public isErrorWrongEmail = false;
  public emailErrorMessage = "";
  public passwordErrorMessage = "";
  public isErrorWrongPassword = false;
  public isInactiveAccount = false;
  public isErrorReset = false;
  public isErrorLogout = false;
  public isverified = false;
  public isChecked = false;
  public responsePayload = new ResponsePayload();
  public openModalEditPassword = false;
  public changePasswordErrorMessage = "";
  public tokenVerificationAccount = new TokenVerificationAccountEntities({
    token: ""
  });
  public verificationAcountErrorMessage = "";
  public showHubList = false;
  public accountId = 0;
  deviceAuthData = new DeviceAuthEntities();
  deviceApprovalData = new DeviceAuthCheckApprovalEntities();
  loginErrorMessage = "";
  errorPasswordExpired  = false;
  showOtpModal = false;
  login2faSessionToken = "";
  login2faMaskedEmail = "";
  login2faOtpExpiresInSeconds = 0;
  login2faOtpErrorMessage = "";
  login2faOtpHasError = false;
  login2faResendAvailableInSeconds = 0;
  isVerify2fa = false;
  dismissedAccountAlerts: Record<string, boolean> = getDismissedAccountAlerts();

  @Mutation
  public setLoading(bool: boolean) {
    this.isLoading = bool;
  }

  @Mutation
  public setAccountId(id: number) {
    this.accountId = id;
  }

  @Mutation
  public setShowHubList(bool: boolean) {
    this.showHubList = bool;
  }

  @Mutation
  public setModalEditPassword(bool: boolean) {
    this.openModalEditPassword = bool;
  }

  @Mutation
  public setModalSuccess(bool: boolean) {
    this.openModalSuccess = bool;
  }

  @Mutation
  public setModalFailed(bool: boolean) {
    this.openModalFailed = bool;
  }

  @Mutation
  public setCloseModal(bool: boolean) {
    this.openModalFailed = bool;
    this.openModalSuccess = bool;
    router.push("/");
  }

  @Mutation
  public setError(bool: boolean) {
    this.isError = bool;
  }

  @Mutation
  public setErrorInternetOrSystem(bool: boolean) {
    this.isErrorInternetOrSystem = bool;
  }

  @Mutation
  public setErrorCause(value: string) {
    this.errorCause = value;
  }

  @Mutation
  public setErrorForget(bool: boolean) {
    this.isErrorForget = bool;
  }

  @Mutation
  public setErrorReset(bool: boolean) {
    this.isErrorReset = bool;
  }

  @Mutation
  public setErrorWrongEmail(bool: boolean) {
    this.isErrorWrongEmail = bool;
  }

  @Mutation
  public setEmailErrorMessage(value: string) {
    this.emailErrorMessage = value;
  }

  @Mutation
  public setErrorPassword(bool: boolean) {
    this.isErrorWrongPassword = bool;
  }

  @Mutation
  public setPasswordErrorMessage(value: string) {
    this.passwordErrorMessage = value;
  }

  @Mutation
  public setInactiveAccount(bool: boolean) {
    this.isInactiveAccount = bool;
  }

  @Mutation
  public setErrorPasswordExpired(bool: boolean) {
    this.errorPasswordExpired = bool;
  }

  @Mutation
  public setChecked(bool: boolean) {
    this.isChecked = bool;
  }

  @Mutation
  public setAuthData(auth: AuthEntities) {
    this.authData = new AuthEntities(); // save temporary in Vuex State
    setCredential({ token: auth.token, expired: auth.expiredAt }); // save permanently in Cookies
  }

  @Mutation
  public setToken(token: string) {
    this.token = token;
  }

  @Mutation
  public SET_RESPONSE_PAYLOAD(res: ResponsePayload) {
    this.responsePayload = res;
  }

  @Mutation
  public setVerified(bool: boolean) {
    this.isverified = bool;
  }

  @Mutation
  public setVerificationAcountErrorMessage(str: string) {
    this.verificationAcountErrorMessage = str;
  }

  @Mutation
  setLoginErrorMessage(value: string) {
    this.loginErrorMessage = value;
  }

  @Mutation
  setShowOtpModal(value: boolean) {
    this.showOtpModal = value;
  }

  @Mutation
  setLogin2faSessionToken(value: string) {
    this.login2faSessionToken = value;
  }

  @Mutation
  setLogin2faMaskedEmail(value: string) {
    this.login2faMaskedEmail = value;
  }

  @Mutation
  setLogin2faOtpExpiresInSeconds(value: number) {
    this.login2faOtpExpiresInSeconds = value;
  }

  @Mutation
  setLogin2faResendAvailableInSeconds(value: number) {
    this.login2faResendAvailableInSeconds = value;
  }

  @Mutation
  setLogin2faOtpErrorMessage(value: string) {
    this.login2faOtpErrorMessage = value;
  }

  @Mutation
  setLogin2faOtpHasError(value: boolean) {
    this.login2faOtpHasError = value;
  }

  @Mutation
  setIsVerify2fa(value: boolean) {
    this.isVerify2fa = value;
  }

  @Mutation
  setAccountAlertDismissed(id: string) {
    const nextDismissedAccountAlerts = {
      ...this.dismissedAccountAlerts,
      [id]: true
    };
    this.dismissedAccountAlerts = nextDismissedAccountAlerts;
    storeDatatoSessionStorage(
      DISMISSED_ACCOUNT_ALERTS_STORAGE_KEY,
      nextDismissedAccountAlerts
    );
  }

  @Mutation
  clearDismissedAccountAlerts() {
    this.dismissedAccountAlerts = {};
    removeDataSessionStorage(DISMISSED_ACCOUNT_ALERTS_STORAGE_KEY);
  }

  @Mutation
  clearLogin2faOtpState() {
    this.login2faSessionToken = "";
    this.login2faMaskedEmail = "";
    this.login2faOtpExpiresInSeconds = 0;
    this.login2faOtpErrorMessage = "";
    this.login2faOtpHasError = false;
    this.login2faResendAvailableInSeconds = 0;
  }

  @Action
  public async sendLogin(params: { email: string; password: string }) {
    this.setLoginErrorMessage("")
    this.setLoading(true);
    if (await this.checkBlacklistedAccount(params.email)) return;
    const presenter = container.resolve(AuthPresenter);
    const deviceID = await getDeviceID(params.email);
    if (!deviceID) await storeDeviceID(params.email);
    presenter
      .onLogin(new LoginApiRequest(params.email, params.password))
      .then(async (res: AuthEntities) => this.handleSuccessLogin({...params, res}))
      .catch(async (error: any) => this.handleFailedLogin({...params, error}))
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  async checkBlacklistedAccount(email: string) {
    const isBlacklisted = isAccountBlacklisted(email);
    if (isBlacklisted) {
      this.setErrorPassword(true);
      this.setLoading(false);
    }
    return isBlacklisted;
  }

  @Action
  public async handleSuccessLogin(params: { email: string; password: string, res: AuthEntities }) {
        this.setAuthData(params.res);
        this.setToken(params.res.token || "");
        if (this.isChecked && params.email && params.password) {
          setRememberMe(params.email, params.password);
        }
        this.setErrorPassword(false);
        this.setErrorWrongEmail(false);
        this.setError(false);
        await AccountController._getData();
        try {
          const cc =
            AccountController.accountData.country_code ||
            AccountController.accountData.account_type_detail.countryCode ||
            "id";
          const locale = (cc?.toLowerCase() || "id").replace(/\s/g, "");
          const localeValue = locale === "my" ? "my" : locale === "id" ? "id" : locale;
          sessionStorage.setItem("countryCode", localeValue);
          syncLocaleFromAccount(cc);
          try {
            (app.config.globalProperties as any).$primevue.config.locale = {
              ...getPrimeVueLocaleConfig(localeValue)
            };
          } catch {
            void 0;
          }
        } catch {
          void 0;
        }
        this.setAccountId(AccountController.accountData.account_id);
        const token: any = jwtDecode(params.res.token);
        this.checkConsoleAccount(token);
        const eventName = IS_SENDER_ACCOUNT
          ? "sender_login_success"
          : "login_success";
        const loginDetail: any = {
          firstLoginAt: new Date(
            AccountController.accountData.account_type_detail.firstLogin
          ).getTime(),
          lastLoginAt: new Date().getTime()
        };
        if (
          AccountController.accountData.account_type_detail.type ===
            "console" ||
          AccountController.accountData.account_type_detail.type ===
            "sub-console"
        ) {
          CargoController.getLastReadCargo();
          CargoController.setLastReadCargoSession({
            start: new Date().getTime(),
            end: new Date(new Date().getTime() + 5 * 60000).getTime()
          });
        }
        dataLayer(eventName, loginDetail, [
          "userId",
          "browserVersion",
          "osVersion",
          "deviceType",
          "userType",
          "timestamp"
        ]);

        MainAppController.setSidebarAndBreadcrumbV2(true);
        if (FlagsPermissionMiddleware.permission_middleware.isEnabled()) {
          MiddlewareController.getTokenMiddleware();
        }

    if (AccountController.accountData.isPosAccount) {
      router.push({
        path: "/dashboard",
        query: { activeMenu: "revenue" }
      });
    }
      }

  @Action
  public async handleSuccessLoginFreshProfile(params: { email: string; password: string, res: AuthEntities }) {
        this.setAuthData(params.res);
        this.setToken(params.res.token || "");
        if (this.isChecked && params.email && params.password) {
          setRememberMe(params.email, params.password);
        }
        this.setErrorPassword(false);
        this.setErrorWrongEmail(false);
        this.setError(false);
        await AccountController._getData(false);
        this.setAccountId(AccountController.accountData.account_id);
        const token: any = jwtDecode(params.res.token);
        this.checkConsoleAccount(token);
        const eventName = IS_SENDER_ACCOUNT
          ? "sender_login_success"
          : "login_success";
        const loginDetail: any = {
          firstLoginAt: new Date(
            AccountController.accountData.account_type_detail.firstLogin
          ).getTime(),
          lastLoginAt: new Date().getTime()
        };
        if (
          AccountController.accountData.account_type_detail.type ===
            "console" ||
          AccountController.accountData.account_type_detail.type ===
            "sub-console"
        ) {
          CargoController.getLastReadCargo();
          CargoController.setLastReadCargoSession({
            start: new Date().getTime(),
            end: new Date(new Date().getTime() + 5 * 60000).getTime()
          });
        }
        dataLayer(eventName, loginDetail, [
          "userId",
          "browserVersion",
          "osVersion",
          "deviceType",
          "userType",
          "timestamp"
        ]);

        MainAppController.setSidebarAndBreadcrumbV2(true);
        if (FlagsPermissionMiddleware.permission_middleware.isEnabled()) {
          MiddlewareController.getTokenMiddleware();
        }

    if (AccountController.accountData.isPosAccount) {
      router.push({
        path: "/dashboard",
        query: { activeMenu: "revenue" }
      });
    }
      }

  @Action
  public async handleFailedLogin(params: {email: string, password: string, error: any}) {
     this.setError(true);
     const errorResponse = params?.error?.response
     const errorMessage = params?.error?.response?.data?.message?.en
     const errorMessageId = params?.error?.response?.data?.message?.id || ""
     const host = sessionStorage.getItem("countryCode") || "";
     const errorMessageBasedOnHost = host === "en" ? errorMessage : errorMessageId;

    if (!errorResponse || errorResponse.status >= 500) {
      this.setErrorInternetOrSystem(true);
      this.setErrorCause(errorResponse ? "server" : "internet");
      return;
    }

    const isBanned = errorMessage.includes("is banned");
    this.setInactiveAccount(isBanned);

    const isInactiveAccount = errorResponse?.status === 422 && errorMessage === "This email is not active";
    this.setInactiveAccount(isInactiveAccount);

    const isWrongEmail = errorResponse?.status === 422 && errorMessage === "This email not registered";
    this.setErrorWrongEmail(isWrongEmail);

    const isPhoneNumberNotFound = errorResponse?.status === 400 && errorMessage === "Phone number not found";

    const isPasswordExpired = errorMessageBasedOnHost.includes("Password expired") || errorMessageBasedOnHost.includes("password has expired");

    if (isPasswordExpired) {
      AccountController.setAccountType(
        errorResponse?.data.data.account_type
      );
      AccountController.setAccountEmail(params.email);
      this.setErrorPasswordExpired(true);
      return;
    }

    if (isPhoneNumberNotFound) {
      this.setErrorWrongEmail(true);
      this.setEmailErrorMessage(params.error.response.data.message.id);
      return;
    }

    // if email/phone number true, error responses for password
    const isWrongPassword = errorResponse?.status === 400 && errorMessage?.toLowerCase().includes("password");
    this.setErrorPassword(isWrongPassword);

    const isAccountInactive = errorMessageBasedOnHost.includes("anda harus melakukan aktivasi ulang oleh") || errorMessageBasedOnHost.includes("deactivated due to inactivity");
    if (isAccountInactive) {
      AccountController.setAccountEmail(params.email);
      await router.push("/inactive-account");
      return;
    }

    if (errorResponse?.status === 403 && errorResponse?.data?.data?.account_type) {
      AccountController.setAccountType(
        errorResponse?.data.data.account_type
      );
      AccountController.setAccountEmail(params.email);
      await router.push("/account/verification-account");
      return;
    }

    // handling error message and redirect page if limit login device
    const errorMessageDevice = errorMessageId?.toLowerCase() || "";
    const target = "login tidak dapat dilakukan karena jumlah perangkat aktif sudah mencapai batas. mohon hubungi helpdesk untuk melanjutkan login.".toLowerCase();

    if (errorMessageDevice.includes(target)) {
      AccountController.setAccountEmail(params.email);
      await router.push("/login-device-limit");
      return;
    }

    if (!this.isInactiveAccount && !this.isErrorWrongPassword && !this.isErrorWrongEmail) {
      this.setLoginErrorMessage(errorMessageBasedOnHost || "")
    }
  }

  @Mutation
  setDeviceAuthData(data: DeviceAuthEntities) {
    this.deviceAuthData = data;
  }
  @Action
  public async sendDeviceAuth(params: DeviceAuthApiRequest) {
    this.setLoginErrorMessage("")
    this.setLoading(true);
    if (await this.checkBlacklistedAccount(params.email)) return;
    const deviceID = await getDeviceID(params.email);
    if (!deviceID) await storeDeviceID(params.email);
    removeDeviceBearer();
    removeTempUserEmail();
    removePairingCode()
    const presenter = container.resolve(AuthPresenter);
    presenter
      .deviceAuth(new DeviceAuthApiRequest(params))
      .then(async (res) => {
        if (this.isChecked) {
          setRememberMe(params.email, params.password);
        }
        if (!res.needApproval) {
          this.handleSuccessLogin({
            email: params.email,
            password: params.password,
            res: res.login
          });
          return;
        }
        this.setDeviceAuthData(res)
        setDeviceBearer(res.approval.token);
        setTempUserEmail(params.email);
        setPairingCode(res.pairingCode)
        router.push("/login-approval");
      })
      .catch(async (error: any) => {
        if(error?.response?.status === 403 && error?.response?.data?.code === 418) {
          router.push("/login-not-allowed");
          return
        }
        this.handleFailedLogin({...params, error})
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Mutation
  setDeviceApprovalData(data: DeviceAuthCheckApprovalEntities) {
    this.deviceApprovalData = data; 
  }
  @Action
  public async sendDeviceAuthCheckApproval() {
    const presenter = container.resolve(AuthPresenter);
    return presenter
      .deviceAuthCheckApproval()
      .then(async (res) => {
        this.setDeviceApprovalData(res);
        if (res.status === STATUS.APPROVED) {
          router.push("/")
          this.handleSuccessLogin({
            email: "",
            password: "",
            res: res.login
          });
          storeUserDevice(getTempUserEmail(), res.device.token);
        }
        if (res.status !== STATUS.PENDING) {
          removeDeviceBearer()
          removeTempUserEmail()
          removePairingCode()
        }
        return res;
      })
  }

  @Action
  public async sendLogin2fa(params: { email: string; password: string }) {
    this.setLoginErrorMessage("");
    this.setLoading(true);
    if (await this.checkBlacklistedAccount(params.email)) return;
    const deviceID = await getDeviceID(params.email);
    if (!deviceID) await storeDeviceID(params.email);
    const presenter = container.resolve(AuthPresenter);
    presenter
      .login2fa(new Login2faApiRequest({ email: params.email, password: params.password }))
      .then(async (res: Login2faEntity) => this.handleLogin2faResponse({ ...params, res }))
      .catch(async (error: any) => this.handleFailedLogin({ ...params, error }))
      .finally(() => { this.setLoading(false); });
  }

  @Action
  public async handleLogin2faResponse(params: { email: string; password: string; res: Login2faEntity }) {
    const { res } = params;
    removeChallengeToken();
    if (res.twoFactorAuthentication.otpRequired) {
      setChallengeToken(res.twoFactorAuthentication.challengeToken);
      if (res.twoFactorAuthentication.enrollment.enrolled) {
        await this.autoRequestOtpAndRedirect(true);
        return;
      }
      if (res.twoFactorAuthentication.enrollment.setupRequired) {
        this.setShowOtpModal(true);
        return;
      }
      removeChallengeToken();
      await this.handleSuccessLogin({ ...params, res: res.authentication });
      return;
    }
    await this.handleSuccessLogin({ ...params, res: res.authentication });
  }

  @Action
  public async autoRequestOtpAndRedirect(isLogin = false) {
    if (isLogin) {
      this.setLoading(true);
    } else {
      MainAppController.showLoading();
    }
    try {
      const captchaToken = await recaptchaV3.executeRecaptcha("login_2fa");
      const challengeToken = getChallengeToken();
      const presenter = container.resolve(AuthPresenter);
      const otpData = await presenter.requestLogin2faOtp(
        new OtpRequestLogin2faApiRequest({ challengeToken, captchaToken })
      );
      this.setLogin2faSessionToken(otpData.token);
      this.setLogin2faMaskedEmail(otpData.maskedEmail);
      this.setLogin2faOtpExpiresInSeconds(otpData.otpExpiresInSeconds);
      this.setLogin2faResendAvailableInSeconds(
        otpData.resendAvailableAfterSeconds
      );
      this.setShowOtpModal(false);
      router.push("/login/verify-otp");
    } catch (error) {
      const errorData = error?.respomse?.data?.data;
      if (errorData) {
        this.setLogin2faSessionToken(errorData.token);
        this.setLogin2faMaskedEmail(errorData.maskedEmail);
        this.setLogin2faOtpExpiresInSeconds(errorData.otpExpiresInSeconds);
        this.setLogin2faResendAvailableInSeconds(
          errorData.resendAvailableAfterSeconds
        );
        this.setShowOtpModal(false);
        router.push("/login/verify-otp");
      }
      if (isLogin) {
        const errorMsg =
          error?.response?.data?.message?.en ||
          "Failed to send OTP. Please try again.";
        if (
          error?.response?.data?.message?.id ===
          "Authorization Bearer dan challenge_token tidak dapat digunakan bersamaan"
        ) {
          removeChallengeToken();
          window.location.reload();
        } else {
          this.setLoginErrorMessage(errorMsg);
        }
      } else {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            error,
            AccountController.accountData.accountIsForeign
              ? "Failed to send OTP!"
              : "Gagal mengirim OTP!",
            () => {
              this.autoRequestOtpAndRedirect();
            }
          )
        );
      }
    } finally {
      if (isLogin) {
        this.setLoading(false);
      } else {
        MainAppController.closeLoading();
      }
    }
  }

  @Action
  public async onOtpSkip() {
    this.setLoading(true);
    try {
      const challengeToken = getChallengeToken();
      const presenter = container.resolve(AuthPresenter);
      const authData = await presenter.verifyLogin2faSkip(
        new OtpVerifySkipApiRequest({ challengeToken })
      );
      removeChallengeToken();
      this.setShowOtpModal(false);
      await this.handleSuccessLoginFreshProfile({ email: "", password: "", res: authData });
    } catch (error) {
      const errorMsg = error?.response?.data?.message?.id
        || error?.response?.data?.message?.en
        || "Sesi verifikasi login kedaluwarsa. Silakan login kembali.";
      this.setShowOtpModal(false);
      this.setLoginErrorMessage(errorMsg);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  public async onOtpActivate() {
    await this.autoRequestOtpAndRedirect(true);
  }

  @Action
  public async verifyLoginOtp(otp: string) {
    this.setLogin2faOtpErrorMessage("");
    this.setLogin2faOtpHasError(false);
    this.setLoading(true);
    this.setIsVerify2fa(true);
    try {
      const challengeToken = getChallengeToken();
      const presenter = container.resolve(AuthPresenter);
      const authData = await presenter.verifyLogin2fa(
        new OtpVerifyLogin2faApiRequest({
          challengeToken,
          token: this.login2faSessionToken,
          otp
        })
      );
      this.clearLogin2faOtpState();
      if (challengeToken) {
        removeChallengeToken();
        await this.handleSuccessLogin({ email: "", password: "", res: authData });
      } else {
        await this.handleSuccessLoginFreshProfile({ email: "", password: "", res: authData });
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        removeChallengeToken();
        this.clearLogin2faOtpState();
        this.setLoginErrorMessage(
          AccountController.accountData.accountIsForeign
            ? "OTP session has expired. Please log in again."
            : "Sesi OTP telah kedaluwarsa. Silakan login kembali."
        );
        router.replace("/");
        return;
      }
      const errorMsgId =
        error?.response?.data?.message?.id ||
        "Kode OTP tidak berlaku. Periksa kembali atau kirim ulang kode.";
      const errorMsgEn =
        error?.response?.data?.message?.en ||
        "The OTP code is invalid. Please check or resend the code.";
      this.setLogin2faOtpErrorMessage(
        AccountController.accountData.accountIsForeign ? errorMsgEn : errorMsgId
      );
      this.setLogin2faOtpHasError(true);
    } finally {
      this.setLoading(false);
      this.setIsVerify2fa(false);
    }
  }

  @Action
  public async resendLoginOtp() {
    this.setLogin2faOtpErrorMessage("");
    this.setLogin2faOtpHasError(false);
    this.setLoading(true);
    try {
      const captchaToken = await recaptchaV3.executeRecaptcha("resend_login_2fa");
      const challengeToken = getChallengeToken();
      const presenter = container.resolve(AuthPresenter);
      const otpData = await presenter.requestLogin2faOtp(
        new OtpRequestLogin2faApiRequest({ challengeToken, captchaToken })
      );
      this.setLogin2faSessionToken(otpData.token);
      if (otpData.maskedEmail) this.setLogin2faMaskedEmail(otpData.maskedEmail);
      this.setLogin2faOtpExpiresInSeconds(otpData.otpExpiresInSeconds);
      this.setLogin2faResendAvailableInSeconds(
        otpData.resendAvailableAfterSeconds
      );
    } catch (error) {
      const status = error?.response?.status;
      if (status === 422) {
        const resendAfter = error?.response?.data?.data?.resend_available_after_seconds ?? 0;
        this.setLogin2faOtpExpiresInSeconds(resendAfter);
        this.setLogin2faResendAvailableInSeconds(resendAfter);
        const errorMsgId =
          error?.response?.data?.message?.id ||
          "Permintaan OTP terlalu cepat. Tunggu sebentar sebelum kirim ulang.";
        const errorMsgEn =
          error?.response?.data?.message?.en ||
          "The OTP request was too fast. Please wait a moment before resending.";
        this.setLogin2faOtpErrorMessage(
          AccountController.accountData.accountIsForeign
            ? errorMsgEn
            : errorMsgId
        );
      } else {
        const errorMsgId =
          error?.response?.data?.message?.id ||
          "Gagal mengirim ulang OTP. Silakan coba lagi.";
        const errorMsgEn =
          error?.response?.data?.message?.en ||
          "Failed to resend OTP. Please try again.";
        this.setLogin2faOtpErrorMessage(
          AccountController.accountData.accountIsForeign
            ? errorMsgEn
            : errorMsgId
        );
      }
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  public _onCheckPassword(params: { email: string; password: string }) {
    const presenter = container.resolve(AuthPresenter);
    return presenter
      .onLogin(new LoginApiRequest(params.email, params.password))
      .then(() => {
        this.setErrorPassword(false);
        return true;
      })
      .catch(error => {
        if (error.response.status === 400) {
          this.setErrorPassword(true);
        }
        return false;
      });
  }

  @Action
  private checkIsAccountHasHub() {
    return new Promise((resolve, reject) => {
      if (AccountController.accountData.hubDetail) {
        setHubData(AccountController.accountData.hubDetail);
        this.handlePairToken().then(() => {
          this.redirectPageHomeConsole();
          reject();
        });
      } else {
        resolve();
      }
    });
  }

  @Action
  public async checkConsoleAccount(param: any) {
    if (window.screen.width < 768) {
      AccountController.setIsCollapseSidebar(true);
    }
    if (param.partner_type === "console") {
      if (!flags.flag_console_select_hub.isEnabled()) {
        setHubData(new Hub());
        this.redirectPageHomeConsole();
        return;
      }
      if (this.isVerify2fa) {
        router.push("/");
      }

      await this.checkIsAccountHasHub();

      await HubController.getHubList(
        new RequestHubList({
          city:
            AccountController.accountData.account_type_detail?.partnerLocation
              ?.city_code,
          status: "active",
          partnerId: AccountController.accountData.account_type_detail?.id
        })
      );
      this.handleConsolePartnerType();
      return;
    }
      FirebaseController.getTokenFirebase()
      .then(currentToken => {
        AccountController.pairToken(
          new PairTokenRequest(currentToken, this.accountId)
        );
      })
      .catch(err => {
        console.log(err);
      });
    this.redirectPageHomePOS();
    return false;
  }

  @Action
  private async handlePairToken() {
    try {
      const currentToken = await FirebaseController.getTokenFirebase();
      await AccountController.pairToken(new PairTokenRequest(currentToken, this.accountId));
    } catch (err) {
      console.log(err);
    }
  }

  @Action
  private async handleConsolePartnerType() {
    if (this.errorHubList) {
      this.setShowHubList(false);
      return;
    }

    if (this.hubList?.length === 1) {
      setHubData(this.hubList[0]);
      await this.handlePairToken();
      this.redirectPageHomeConsole();
      return;
    }

    if (!this.hubList?.length) {
      setHubData(new Hub());
      await this.handlePairToken();
      this.redirectPageHomeConsole();
      return;
    }

    this.setShowHubList(true);
  }
  @Action
  public redirectPageHomeConsole() {
    router.replace(flags.flag_default_path_genesis.getValue());
  }

  @Action
  public redirectPageHomePOS() {
    router.replace(flags.flag_default_path_genesis.getValue());
  }

  @Action
  public setRemember() {
    this.setChecked(true);
  }

  @Action
  public sendEmail(params: { email: string }) {
    this.setLoading(true);
    const presenter = container.resolve(AuthPresenter);
    return presenter
      .forgotPassword(new ForgotPasswordApiRequest(params.email))
      .then((res: ResponsePayload) => {
        this.SET_RESPONSE_PAYLOAD(res);
        this.setVerified(true);
        this.setModalSuccess(true);
        return {
          success: true,
          resp: res
        };
      })
      .catch((err: any) => {
        if (err.response === undefined) {
          this.setErrorInternetOrSystem(true);
          this.setErrorCause(err.response ? "server" : "internet");
        } else {
          if (err.response.status === 422) {
            this.setErrorForget(true);
          }

          if (err.response.status === 400) {
            this.setModalSuccess(true);
          }
        }
        return {
          success: false,
          resp: err
        };
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public editPassword(params: {
    payload: EditPasswordApiRequest;
    callback?: () => void;
  }) {
    MainAppController.showLoading();
    const presenter = container.resolve(AuthPresenter);
    return presenter
      .editPassword(params.payload)
      .then((res: ResponsePayload) => {
        this.SET_RESPONSE_PAYLOAD(res);
        this.setModalEditPassword(true);
        return true;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "", undefined, params?.callback)
        );
        this.setErrorForget(true);
        return false;
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public changePassword(params: {
    email: string;
    password: string;
    token: string;
  }) {
    MainAppController.showLoading();
    const presenter = container.resolve(AuthPresenter);
    presenter
      .resetPassword(
        new ResetPasswordApiRequest(params.email, params.password, params.token)
      )
      .then((res: ResponsePayload) => {
        this.SET_RESPONSE_PAYLOAD(res);
        this.setModalSuccess(true);
      })
      .catch((error: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Password Reset Failed !", () => {
            MainAppController.closeErrorMessage();
            this.changePassword(params);
          })
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  tokenResponse = new SetupPasswordValidation();

  @Action
  public async validateSetupPasswordLink(
    payload: SetupPasswordValidationApiRequest
  ) {
    try {
      MainAppController.showLoading();
      MainAppController.closeMessageModal();

      const res = await container
        .resolve(AuthPresenter)
        .validateSetupPasswordLink(payload);

      this.setTokenResponse(res);
    } catch (error) {
      const parseError = parsingErrorResponse(
        error,
        "Validasi Token Gagal",
        undefined
      );

      if (parseError.type === CLIENT_ERROR && parseError.code !== 404) {
        this.setTokenResponse(
          new SetupPasswordValidation({
            success: false,
            code: parseError.code
          })
        );
        return;
      }

      MainAppController.showMessageModal(
        new ModalMessageEntities({
          ...parseError,
          textCancel: "Kembali",
          onClose: () => router.replace({ name: "login" }),
          textSuccess: "Coba lagi",
          onSubmit: () => this.validateSetupPasswordLink(payload)
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }
  @Mutation
  public setTokenResponse(payload: SetupPasswordValidation) {
    this.tokenResponse = payload;
  }

  @Action
  public async resendSetupPasswordLink(
    payload: SetupPasswordValidationApiRequest
  ) {
    try {
      MainAppController.showLoading();
      MainAppController.closeMessageModal();

      await container.resolve(AuthPresenter).resendSetupPasswordLink(payload);

      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: "A new link has been sent.",
          message:
            "Please check your inbox or spam folder in your email to set a new password.",
          textSuccess: "Login to Genesis",
          image: "ill_badge_success",
          onSubmit: () => router.replace({ name: "login" })
        })
      );
    } catch (error) {
      const parseError = parsingErrorResponse(
        error,
        "Link baru gagal dikirim",
        undefined
      );

      MainAppController.showMessageModal(
        new ModalMessageEntities({
          ...parseError,
          textCancel: "Kembali",
          onClose: MainAppController.closeMessageModal,
          textSuccess: "Coba lagi",
          onSubmit: () => this.validateSetupPasswordLink(payload)
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  @Action
  async onLoggingOut() {
    BalanceController.onResetBalance();
    this.clearDismissedAccountAlerts();
    clearDataLocalStorage();
    clearDataSessionStorage();
    HelpCenterController.clearCache();
    removeAuthCredential();
    PusatResolusiV2Controller.resetFilter();
    AccountController.setAccountData(new AccountEntities());
    LocationController.setDetailDistrict(new DistrictData());
    AccountController.setActiveCasbackConfig(new ResponsePayloadV2());
    AccountController.setActiveTopupFeeConfig(new ResponsePayloadV2());
    storeDatatoLocalStorage("logout-event", Date.now().toString()); // To synchronize logout across tabs (even duplicated ones)
    localStorage.setItem("shouldShowChangePassword", "true");
    PrintReceiptController.resetListSttSuccessBooking();
    PrintReceiptController.setLastDateSttSuccessBooking();
    await router.replace({ name: "login" });
  }
  @Action
  public async logout() {
    this.setError(false);
    this.setErrorCause("");
    this.setLoading(true);
    this.setAccountId(AccountController.accountData.account_id);
    try {
      await AccountController.unpairToken(
        new UnpairTokenRequest(this.accountId)
      );
    } catch (e) {
      console.warn(e);
    }
    const presenter = container.resolve(AuthPresenter);
    return presenter
      .logout(new LogoutApiRequest())
      .then(async (res: ResponsePayload) => {
        this.SET_RESPONSE_PAYLOAD(res);
        NotificationController.setIsOpen(false);
        await this.onLoggingOut();
        return true;
      })
      .catch(async (error: any) => {
        if (error.response) {
          await this.onLoggingOut();
        } else {
          this.setError(true);
          this.setErrorCause(error.response ? "server" : "internet");
        }
        return false;
      })
      .finally(() => {
        this.setLoading(false);
        this.setShowHubList(false);
      });
  }

  // verification account
  @Action
  public async verificationAccount(params: {
    emailOrUsername: string;
    question: string;
    answer: string;
    captchaToken: string;
    isFromOTP?: boolean;
  }) {
    this.setLoading(true);
    const presenter = container.resolve(AuthPresenter);
    return presenter
      .verificationAccount(
        new VerificationAccountRequest(
          params.emailOrUsername,
          params.question,
          params.answer,
          params.captchaToken
        )
      )
      .then((res: TokenVerificationAccountEntities) => {
        this.setError(false);
        this.setErrorCause("");
        // router.replace(
        //   `/account/reset-password?token=${res.token}&email=${params.emailOrUsername}`
        // );
        if (!params.isFromOTP) {
          router.replace({
            path: "/account/verification-otp",
            query: {
              token: res.token,
              email: res.emailUser,
              "email_partner": res.emailPartnerClient,
              "phone_number": res.phoneNumberPartnerClient,
              username: params.emailOrUsername,
              question: params.question,
              answer: params.answer
            }
          });
        }
        return res;
      })
      .catch((error: any) => {
        this.setError(true);
        this.setVerificationAcountErrorMessage(
          resolveApiBilingualMessage(error.response?.data?.message) || ""
        );
        if (!(error.response?.status <= 500)) {
          this.setErrorInternetOrSystem(true);
          this.setError(false);
          this.setErrorCause(error.response ? "server" : "internet");
        }
        throw error;
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public async requestOtp(params: OtpRequest) {
    const presenter = container.resolve(AuthPresenter);
    return presenter.requestOtp(params);
  }

  @Action
  public handleError() {
    this.setError(false);
    this.setErrorInternetOrSystem(false);
    this.setInactiveAccount(false);
  }

  @Action
  public handleErrorLogout() {
    this.setError(false);
  }

  @Action
  public async verifyOTP(payload: VerificationOTPRequest) {
    const presenter = container.resolve(AuthPresenter);
    return presenter.verificationOTP(payload);
  }

  @Action
  public async setupPassword(payload: SetupPasswordRequest) {
    const presenter = container.resolve(AuthPresenter);
    return presenter.setupPassword(payload);
  }

  get hubList() {
    return HubController.hubList.data;
  }

  get errorHubList() {
    return HubController.isError;
  }
}

export const AuthController = getModule(AuthStore);
